import { CoachingPracticeAudio } from '@euler/model/coaching-practice';
import { useServiceFactory } from '@euler/services/factory';
import { useCreation } from '@euler/utils/hooks/useCreation';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { BehaviorSubject } from 'rxjs';
import { useClipPlayer } from './useClipPlayer';

/**
 * A customer line played again on tap, muted or not: from the clips
 * already heard when `known` has them, else voiced again for `attemptId`
 * (null: only what is known can play) and handed to `onVoiced`. The latest
 * tap wins.
 */
export function useLineReplay(
  attemptId: string | null,
  known?: (turn: number) => CoachingPracticeAudio[] | undefined,
  onVoiced?: (clips: CoachingPracticeAudio[]) => void,
) {
  const { coachingService } = useServiceFactory();
  // A tap is an explicit ask to hear the line.
  const speak$ = useCreation(() => new BehaviorSubject(true), []);
  const player = useClipPlayer(speak$);
  const playing$ = useCreation(
    () => new BehaviorSubject<number | null>(null),
    [],
  );
  const request = useRef(0);
  const loading = useRef(false);
  const lookup = useRef(known);
  lookup.current = known;
  const voiced = useRef(onVoiced);
  voiced.current = onVoiced;
  useEffect(() => {
    const subscription = player.playing$.subscribe(on => {
      if (!on && !loading.current) playing$.next(null);
    });
    return () => subscription.unsubscribe();
  }, [player, playing$]);

  const stop = useCallback(() => {
    request.current += 1;
    loading.current = false;
    player.interrupt();
    playing$.next(null);
  }, [player, playing$]);

  const play = useCallback(
    async (turn: number) => {
      // A second tap on the line playing stops it.
      if (playing$.getValue() === turn) {
        stop();
        return;
      }
      const mine = ++request.current;
      player.interrupt();
      const heard = lookup.current?.(turn);
      if (heard?.some(x => x.audio)) {
        loading.current = false;
        playing$.next(turn);
        player.enqueue(heard);
        return;
      }
      if (!attemptId) return;
      loading.current = true;
      playing$.next(turn);
      try {
        const clips = await coachingService.voicePracticeLine(attemptId, turn);
        if (mine !== request.current) return;
        loading.current = false;
        if (clips.some(x => x.audio)) {
          voiced.current?.(clips);
          player.enqueue(clips);
        } else playing$.next(null);
      } catch (error) {
        console.error('coaching line voice failed', error);
        if (mine !== request.current) return;
        loading.current = false;
        playing$.next(null);
      }
    },
    [attemptId, coachingService, player, playing$, stop],
  );

  return useMemo(
    () => ({ playing$, progress$: player.progress$, play, stop }),
    [playing$, play, player, stop],
  );
}
